import { throttle } from 'lodash';
import mapboxgl from 'mapbox-gl';
import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { EditorMode } from '~/common/types';
import SurfaceCanvas from '~/components2/Map/canvas/PassthroughCanvas';
import layerStore from '~/store2/layers';
import type { MapStoreBase } from '.';
import { calcBoundingBox, getIdentifiableLayerIds } from './Widgets/MetadataSidebar/metadataSidebar.helpers';

interface Props {
  store: MapStoreBase;
}

export default function PassthroughSurface({ store }: Props) {
  const map = useMap();
  const containerDiv = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (containerDiv.current && map.current) {
      const mapbox = map.current.getMap();
      const surface = new SurfaceCanvas({
        id: `main-surface-${nanoid()}`,
        container: containerDiv.current,
        map: mapbox,
        store: store,
      });

      // Pointer affordance: this surface canvas sits ON TOP of the mapbox canvas, so a cursor set on
      // the map canvas is masked. Set it on the surface itself when hovering a clickable feature —
      // same editorMode gate + 10px hit-test as the left-click select (handleMapClick).
      let lastCursor = '';
      // Per-move hit-test (getIdentifiableLayerIds + queryRenderedFeatures) is too heavy to run on
      // every mousemove; ~1 frame (16ms) is plenty for a cursor affordance.
      const onHoverCursor = throttle((e: MouseEvent) => {
        let next = 'default';
        if (store.editorMode === EditorMode.None && e.buttons === 0 && !mapbox.isMoving()) {
          const layers = getIdentifiableLayerIds(mapbox, layerStore);
          const { x, y } = surface.screenCoordsFromEvent(e);
          if (
            layers.length > 0 &&
            mapbox.queryRenderedFeatures(calcBoundingBox(new mapboxgl.Point(x, y), 10), { layers }).length > 0
          ) {
            next = 'pointer';
          }
        }
        if (lastCursor !== next) {
          lastCursor = next;
          surface.setCursor(next);
        }
      }, 16);
      surface.canvas.addEventListener('mousemove', onHoverCursor);

      return () => {
        onHoverCursor.cancel();
        surface.canvas.removeEventListener('mousemove', onHoverCursor);
        surface.destroy();
      };
    }
  }, []);

  if (!map.current) {
    return null;
  }

  return <div style={{ cursor: '-moz-grab' }} className={'absolute left-0 top-0'} ref={containerDiv}></div>;
}
